import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ActionButton, Buttons, CancelButton, Drawer, Table } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useForm, useIsMounted } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import OptionSelect from './OptionSelect';

export default function PaymentMethodMappings({ onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();
  const [{ isSubmitting, saved }, form] = useForm();
  const isMounted = useIsMounted();
  const [dirty, setDirty] = useState(false);
  const dirtyCheck = useDirtyCheck(() => dirty);
  const { sageConnectionId } = useParams();

  const [{ methods, values, isReady }, setState] = useState({ methods: null, isReady: false });

  useEffect(() => {
    (async () => {
      const { data: methods } = await api.www
        .workspaces(workspace.id)
        .sage(sageConnectionId)
        .getPaymentMethodMappings();
      const { data: values } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getPaymentMethodValues();
      if (!isMounted.current) return;
      setState({ methods, values, isReady: true });
    })();
  }, [api, workspace.id, isMounted, sageConnectionId]);

  const updateMapping = ({ paymentMethodId, sagePaymentMethodValue }) => {
    setDirty(true);
    setState((state) => ({
      ...state,
      methods: state.methods.map((m) => (m.id === paymentMethodId ? { ...m, sagePaymentMethodValue } : m)),
    }));
  };

  return (
    <Drawer
      isOpen
      title="Payment Method Mappings"
      width="70rem"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        const handleSave = async () => {
          form.submit();
          try {
            const mappings = methods.map((m) => ({
              paymentMethodId: m.id,
              sagePaymentMethodValue: m.sagePaymentMethodValue,
            }));
            await api.www.workspaces(workspace.id).sage(sageConnectionId).updatePaymentMethodMappings(mappings);
            form.save();
            closeDrawer();
          } catch (error) {
            toast.error(error.message);
            form.done();
          }
        };

        if (!isReady) return <PageLoader />;

        return (
          <>
            <Table style={{ marginBottom: '2rem' }}>
              <Table.BoxHeader>
                <Table.Column width="40%">Payment Method</Table.Column>
                <Table.Column>Sage Payment Method</Table.Column>
              </Table.BoxHeader>
              <Table.Body>
                {methods.map((method) => (
                  <Table.BoxRow key={method.id}>
                    <Table.Cell>{method.name}</Table.Cell>
                    <Table.Cell>
                      <OptionSelect
                        defaultOptions={values}
                        typeLabel="Sage Payment Method"
                        value={method.sagePaymentMethodValue}
                        onChange={(event) =>
                          updateMapping({
                            paymentMethodId: method.id,
                            sagePaymentMethodValue: event.target.value || null,
                          })
                        }
                      />
                    </Table.Cell>
                  </Table.BoxRow>
                ))}
              </Table.Body>
            </Table>
            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                <ActionButton isLoading={isSubmitting} ok={saved} onClick={handleSave}>
                  Save &amp; Close
                </ActionButton>
              </Buttons>
            </Drawer.Actions>
          </>
        );
      }}
    </Drawer>
  );
}
